import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, setAccessToken, getAccessToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!getAccessToken()) {
      setUser(null);
      setLoading(false);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setAccessToken(null);
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If the URL contains a session_id fragment, AuthCallback will handle it
    // and call refresh itself. Skip the /me check here to avoid a race.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const setAuth = useCallback((token, userObj) => {
    setAccessToken(token || null);
    setUser(userObj || null);
    setLoading(false);
  }, []);

  const signOut = useCallback(() => {
    setAccessToken(null);
    setUser(null);
  }, []);

  const value = { user, loading, setAuth, refresh, refreshUser: refresh, signOut };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}

/**
 * Compute the route a logged-in user should land on.
 * - rejected   → /rejected
 * - pending    → /pending-approval
 * - admin      → /admin
 * - teacher    → /teacher
 * - parent     → /parent
 * - student    → /student
 */
export function routeForUser(user) {
  if (!user) return "/login";
  if (user.status === "rejected") return "/rejected";
  if (user.status !== "approved") return "/pending-approval";
  if (user.role === "admin") return "/admin";
  if (user.role === "teacher") return "/teacher";
  if (user.role === "parent") return "/parent";
  return "/student";
}
